/**
 * Service for managing saved filters
 */
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiUtils';

/**
 * Save a new filter configuration
 * @param {object} filterData - The filter data to save
 * @param {string} filterData.name - Name of the filter
 * @param {string} filterData.description - Optional description
 * @param {string} filterData.filterId - ID of the filter whose results to save
 * @param {Object} filterData.overrides - Optional user overrides for filter decisions
 * @param {boolean} filterData.isFavorite - Whether the filter is marked as favorite
 * @returns {Promise<Response>} - API response
 */
export async function saveFilter(filterData) {
  try {
    const { name, description, filterId, originalFilterId, filterConfig: passedFilterConfig, overrides, isFavorite } = filterData;
    
    console.log('saveFilter received data:', { name, description, filterId, overrides, isFavorite });
    console.log('CRITICAL: Received filterConfig from SaveFilterDialog:', passedFilterConfig);
    
    if (!name || !filterId) {
      throw new Error('Filter name and ID are required');
    }
    
    // Check if overrides is a valid object
    if (overrides) {
      console.log('Overrides present:', typeof overrides, 'with keys:', Object.keys(overrides));
    } else {
      console.log('No overrides received or overrides is undefined/null');
    }
    
    // APPROACH 2: Fetch and merge overrides from the original filter if provided
    let combinedOverrides = overrides || {};
    
    if (originalFilterId) {
      try {
        console.log('Fetching original filter overrides from ID:', originalFilterId);
        const originalFilterResponse = await getSavedFilterById(originalFilterId);
        
        if (originalFilterResponse.ok) {
          const originalFilterData = await originalFilterResponse.json();
          
          if (originalFilterData && originalFilterData.filter && originalFilterData.filter.overrides) {
            const originalOverrides = originalFilterData.filter.overrides;
            console.log('Retrieved original overrides:', originalOverrides);
            
            // Merge original overrides with new overrides
            combinedOverrides = mergeOverrides(originalOverrides, overrides || {});
            console.log('Combined overrides after merging with original:', combinedOverrides);
          } else {
            console.log('No overrides found in original filter');
          }
        } else {
          console.warn('Could not retrieve original filter data for merging overrides');
        }
      } catch (error) {
        console.error('Error fetching original filter overrides:', error);
        // Continue with just the current overrides if there's an error
      }
    } else {
      console.log('No originalFilterId provided, using only current overrides');
    }
    
    // First, get the progress data which contains the original filter configuration
    const progressResponse = await apiGet(`/api/progress/${filterId}`);
    if (!progressResponse.ok) {
      throw new Error('Could not retrieve filter configuration');
    }
    
    const progressData = await progressResponse.json();
    console.log('Progress data from API:', progressData);
    
    // Now get the results data which contains column information
    const resultsResponse = await apiGet(`/api/results/${filterId}`);
    if (!resultsResponse.ok) {
      throw new Error('Could not retrieve filter results');
    }
    
    const resultsData = await resultsResponse.json();
    console.log('Results data from API:', resultsData);
    
    // Extract the complete filter configuration from progress data
    // This will contain all settings from the Configure Filters page
    const originalConfig = progressData.filterConfig || {};
    console.log('Original filter config from progress data:', originalConfig);
    
    // Get reasoning text from localStorage - this is our single source of truth
    let reasoningText = localStorage.getItem('filterReasoningText') || '';
    console.log('Retrieved reasoning text from localStorage:', reasoningText ? reasoningText.substring(0, 50) + '...' : 'None');
    
    // Prepare filter data for saving with standardized columnConfig structure
    
    // Get processed columns ONLY from llmValueDecisions - our SINGLE source of truth
    const selectedColumns = resultsData && resultsData.llmValueDecisions ? 
      Object.keys(resultsData.llmValueDecisions) : [];
    console.log('Using llmValueDecisions as single source of truth for columns:', selectedColumns);
    
    // If we have no columns from our source of truth, we should fail clearly rather than using inconsistent fallbacks
    if (selectedColumns.length === 0) {
      throw new Error('Cannot save filter: No column data found in filter results');
    }
    
    // Use the column config from the passed filter configuration as our single source of truth
    // This completely trusts the passed configuration from SaveFilterDialog
    
    // Start with the column config from passed filter config
    const columnConfig = {};
    
    // Simply ensure each selected column has an entry in the column config
    selectedColumns.forEach(colName => {
      if (passedFilterConfig && passedFilterConfig.columnConfig && passedFilterConfig.columnConfig[colName]) {
        // Use configuration directly from SaveFilterDialog
        columnConfig[colName] = passedFilterConfig.columnConfig[colName];
      } else {
        // Fallback for any missing columns
        columnConfig[colName] = { instructions: '' };
      }
    });
    
    // Log column configuration for debugging
    console.log('Using column config from SaveFilterDialog:',
      Object.keys(columnConfig).map(col => ({ 
        column: col, 
        hasInstructions: !!columnConfig[col]?.instructions 
      })));
    
    // Final column config is exactly what we just created
    const finalColumnConfig = columnConfig;
    
    const saveData = {
      name,
      description: description || '',
      filterConfig: {
        // Take all properties from the original config to ensure we don't lose any settings
        ...originalConfig,
        // First use any passed filterConfig properties (from SaveFilterDialog)
        ...(passedFilterConfig || {}),
        // Add the standardized columnConfig structure with passed column instructions
        columnConfig: finalColumnConfig,
        // Override with any specific properties we know we need
        selectedColumns: selectedColumns,
        reasoningText: reasoningText, // Use our localStorage value as the primary source
        model: originalConfig.model || resultsData.model || 'o4-mini-high',
        temperature: originalConfig.temperature || resultsData.temperature || 0.7,
        chunkSize: originalConfig.chunkSize || resultsData.chunkSize || 300
      },
      overrides: combinedOverrides, // Include the combined overrides from original filter and current session
      isFavorite: isFavorite || false
    };
    
    console.log('Final columnConfig structure:', saveData.filterConfig.columnConfig);
    
    console.log('Saving filter with config:', saveData);
    
    // Save to server
    return apiPost('/api/saved-filters', saveData);
  } catch (error) {
    console.error('Save filter error:', error);
    // Create a failed response object
    return new Response(JSON.stringify({ error: error.message }), {
      status: 400,
      headers: { 'Content-Type': 'application/json' }
    });
  }
}

/**
 * Get all saved filters for the current user
 * @returns {Promise<Response>} - API response
 */
export async function getSavedFilters() {
  return apiGet('/api/saved-filters');
}

/**
 * Get a specific saved filter by ID
 * @param {string} filterId - The filter ID
 * @returns {Promise<Response>} - API response
 */
export async function getSavedFilterById(filterId) {
  return apiGet(`/api/saved-filters/${filterId}`);
}

/**
 * Update an existing saved filter
 * @param {string} filterId - The filter ID
 * @param {object} filterData - The updated filter data
 * @returns {Promise<Response>} - API response
 */
export async function updateSavedFilter(filterId, filterData) {
  return apiPut(`/api/saved-filters/${filterId}`, filterData);
}

/**
 * Delete a saved filter
 * @param {string} filterId - The filter ID
 * @returns {Promise<Response>} - API response
 */
export async function deleteSavedFilter(filterId) {
  return apiDelete(`/api/saved-filters/${filterId}`);
}

/**
 * Toggle the favorite status of a filter
 * @param {string} filterId - The filter ID
 * @returns {Promise<Response>} - API response
 */
export async function toggleFavorite(filterId) {
  return apiPost(`/api/saved-filters/${filterId}/toggle-favorite`);
}

/**
 * Helper function to properly merge original and new overrides
 * @param {Object} originalOverrides - Overrides from the original filter
 * @param {Object} newOverrides - New overrides from the current session
 * @returns {Object} - Merged overrides object
 */
function mergeOverrides(originalOverrides, newOverrides) {
  // Start with a deep copy of the original overrides
  const merged = JSON.parse(JSON.stringify(originalOverrides || {}));
  
  // Merge in the new overrides at the column level
  for (const column in newOverrides) {
    if (!merged[column]) {
      merged[column] = {};
    }
    
    // Merge the value-level overrides for this column
    Object.assign(merged[column], newOverrides[column]);
  }
  
  return merged;
}
